// Super-admin banner on the Schedules tab: schedules whose last run failed or
// returned no rows, with the weekly digest controls.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function ScheduleHealthBanner({ refreshKey }) {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/report-hub/health").then((r) => setHealth(r.data)).catch(() => setHealth(null));
  }, [refreshKey]);

  if (!health?.visible) return null;
  const ok = health.problems === 0;

  const digest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/report-hub/health/digest");
      toast.success(data.status === "healthy" ? "Every schedule is healthy — nothing to send"
        : data.status === "paused" ? `Digest generated (${data.rows} schedule(s)) — outbound email is paused`
          : `Digest sent to ${(data.recipients || []).length} super admin(s)`);
      const r = await api.get("/report-hub/health");
      setHealth(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the digest"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${
      ok ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30"
         : "border-amber-200 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/30"}`}
         data-testid="schedule-health-banner">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
      <div className="min-w-0 flex-1 text-sm">
        <span className={`font-semibold ${ok ? "text-emerald-800 dark:text-emerald-200" : "text-amber-900 dark:text-amber-100"}`} data-testid="schedule-health-summary">
          {ok ? "All schedules healthy"
            : `${health.problems} schedule${health.problems === 1 ? "" : "s"} need${health.problems === 1 ? "s" : ""} attention`}
        </span>
        <span className="ml-2 text-xs text-slate-500">
          {ok ? `No failures or empty runs in the last ${health.days} days.`
            : `${health.failed} failing · ${health.empty} returned no rows (last ${health.days} days).`}
          {" "}Super admins get a digest every Monday 08:30 when anything is listed
          {health.digest_last_sent_at ? ` · last digest ${new Date(health.digest_last_sent_at).toLocaleString()}` : ""}.
        </span>
      </div>
      <Button size="sm" variant="outline" asChild data-testid="schedule-health-open">
        <Link to="/reports/schedule_health">Open health report</Link>
      </Button>
      <Button size="sm" variant="outline" onClick={digest} disabled={busy} data-testid="schedule-health-digest-now">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email digest now
      </Button>
    </div>
  );
}
